/**
 * Site-wide footer (#126, FR-13.1). Rendered on every surface via the App layout so the
 * legal pages — Terms, Privacy, Refund — are reachable from anywhere without editing the
 * URL, and the operating entity is always disclosed. Links are plain router `Link`s to
 * the static policy routes; each carries a stable `data-testid` for e2e navigation.
 *
 * It also hosts the generic **"Feedback" entry point** (#200, FR-16.2 / DI-E): a button
 * next to the legal links that opens the {@link FeedbackModal}. Placed here so it's
 * reachable from every surface (incl. by guests), consistent with the legal links, and
 * non-intrusive. The modal is only mounted while open (closed on SSR/prerender).
 *
 * It also carries the SEO internal-linking groups (#353, FR-15.7): a **Create Games** group
 * (the four product-intent Jeopardy-style landing pages) and a **Trivia** group (the
 * `/trivia` content hub), so the two SEO systems are linked bidirectionally from every
 * surface without overloading the primary nav (issue §11).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND_NAME } from './brand';
import { LEGAL_ENTITY } from './legal/legal';
import { FeedbackModal } from './FeedbackModal';

/** The Create-Games footer group — the four product-intent landing pages (#353). */
const CREATE_GAME_LINKS: { to: string; label: string }[] = [
  { to: '/online-jeopardy', label: 'Online Jeopardy-Style Game' },
  { to: '/jeopardy-game-maker', label: 'Jeopardy Game Maker' },
  { to: '/multiplayer-jeopardy', label: 'Multiplayer Jeopardy' },
  { to: '/custom-jeopardy-games', label: 'Custom Jeopardy Games' },
];

export function Footer(): React.JSX.Element {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  return (
    <footer className="app-footer" data-testid="app-footer">
      <nav className="app-footer-seo" aria-label="Explore" data-testid="footer-seo">
        <div className="app-footer-seo__group">
          <h2 className="app-footer-seo__head">Create Games</h2>
          <ul>
            {CREATE_GAME_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="app-footer-seo__group">
          <h2 className="app-footer-seo__head">Trivia</h2>
          <ul>
            <li>
              <Link to="/trivia" data-testid="footer-trivia">
                Browse Trivia Topics
              </Link>
            </li>
          </ul>
        </div>
      </nav>
      <nav className="app-footer-links" aria-label="Footer">
        <Link to="/faq" data-testid="footer-faq">
          FAQ
        </Link>
        <Link to="/terms" data-testid="footer-terms">
          Terms of Service
        </Link>
        <Link to="/privacy" data-testid="footer-privacy">
          Privacy Policy
        </Link>
        <Link to="/refunds" data-testid="footer-refunds">
          Refund Policy
        </Link>
        <button
          type="button"
          className="fb-link"
          data-testid="footer-feedback"
          onClick={() => setFeedbackOpen(true)}
        >
          Feedback
        </button>
      </nav>
      <p className="app-footer-entity">
        {BRAND_NAME} is operated by {LEGAL_ENTITY}.
      </p>
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </footer>
  );
}
